import {useEffect} from 'react';

import ButtonRaw from '@/components/Button/ButtonRaw';
import RemoteIconV2 from '@/components/Icon/RemoteIconV2';
import appConfig from '@/config/appConfig.typed';
import useConfigExternalLink from '@/hooks/externalLink/useConfigExternalLink';
import ageGateService from '@/services/ageGate/ageGateService';
import appContent from '@/services/content/appContent.typed';
import {formatHtmlWithComponentsOffset, formatString} from '@/utils/stringUtils';

type AgeGateLockedProps = {
    onUnlocked: () => void;
};

export const AgeGateLocked = ({onUnlocked}: AgeGateLockedProps) => {
    const {topIcon, icon, title, description, linkLabel, externalLink} = appContent.getAgeGateLockedContent() || {};
    const {lockTime, minPassingAge} = appConfig.getAgeGateSettings();
    const {redirectToExternalLink} = useConfigExternalLink(externalLink);

    useEffect(() => {
        const remainingMs = ageGateService.getRemainingLockTime();

        if (remainingMs <= 0) {
            onUnlocked();
            return;
        }

        const timer = setTimeout(() => onUnlocked(), remainingMs);

        return () => clearTimeout(timer);
    }, [onUnlocked]);

    const textDescription = formatString(description, minPassingAge, lockTime);

    const linkElement =
        externalLink && linkLabel ? (
            <ButtonRaw
                // @ts-ignore ButtonRaw is untyped JS
                className='inline underline'
                onClick={redirectToExternalLink}
                dangerouslySetInnerHTML={{__html: linkLabel}}
            />
        ) : null;

    const descriptionContent = linkElement
        ? formatHtmlWithComponentsOffset(2, textDescription, linkElement)
        : textDescription;

    return (
        <div className='flex flex-1 flex-col bg-primary-dark px-6 text-romance pb-safe-offset-8 pt-safe-offset-8'>
            {topIcon && <RemoteIconV2 icon={topIcon} className='shrink-0 self-center pt-7 md:w-[151px] md:pt-8' />}
            <div className='flex flex-1 flex-col items-center justify-center gap-2 self-center md:w-[640px] md:gap-4'>
                {icon && <RemoteIconV2 icon={icon} className='size-16' />}
                {title && <h1 className='m-0 text-center text-32 font-bold leading-12'>{title}</h1>}
                {descriptionContent &&
                    (typeof descriptionContent === 'string' ? (
                        <p
                            className='text-center text-16 leading-15'
                            dangerouslySetInnerHTML={{__html: descriptionContent}}
                        />
                    ) : (
                        <p className='text-center text-16 leading-15'>{descriptionContent}</p>
                    ))}
            </div>
        </div>
    );
};
